/**
 * classifyValueCellKind — pure classifier for INSERT VALUES cell tone
 * (DML-35, Advisor 2 §A7 third bullet). Distinguishes string / numeric /
 * expression cell text so `ValuesCellPill` and `ValuesRowsGrid`'s read-only
 * span path can apply a subtle color tone — the quotes around a string
 * literal are load-bearing and easy to lose when editing inline, so making
 * the kind visually legible helps.
 *
 * Rules (deliberately simple — a tone, not a parser):
 *  - a leading quote introducer → `'string'`: a bare `'…'`, the common
 *    engine-specific prefixed forms `N'…'` (MSSQL nvarchar), `X'…'`/`x'…'`
 *    and `B'…'`/`b'…'` (hex/bit-string literal prefixes), and `"…"` (some
 *    engines treat a double-quoted token as a string literal). Errs toward
 *    `'string'` for any quote-introduced token rather than risk misreading
 *    it as an identifier.
 *  - a pure numeric literal (optional sign, optional decimal point, optional
 *    exponent) → `'number'`.
 *  - everything else (function calls, `DEFAULT`, `NULL`, bare identifiers,
 *    arithmetic expressions) → `'expression'`.
 *
 * Pure, no React — colocated with the grid it tones.
 */

export type ValueCellKind = 'string' | 'number' | 'expression';

// Optional single-letter string-literal prefix (case-insensitive: N/X/B),
// immediately followed by a quote character. The apostrophe in the character
// class is written as the escape sequence backslash-u-0027 rather than a raw
// apostrophe character -- purely a raw-source nicety so the folder-wide
// codename-leak scanner's naive quote-pairing scan (vqeCopy.test.ts) does not
// misread a lone apostrophe as an unterminated string-literal opener and
// swallow the rest of the file; the regex's matching behavior is unchanged.
const QUOTE_INTRODUCER_RE = /^(?:[NXB])?[\u0027"]/i;
const NUMBER_RE = /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/;

export function classifyValueCellKind(text: string): ValueCellKind {
  const trimmed = text.trim();
  if (QUOTE_INTRODUCER_RE.test(trimmed)) return 'string';
  if (NUMBER_RE.test(trimmed)) return 'number';
  return 'expression';
}

/**
 * Tailwind tone classes per kind — reuses the SAME string/number hues as
 * `SqlPreviewCode`'s token palette (`render/common/SqlPreviewCode.tsx`,
 * lane-g's `TOKEN_CLASSES`) so the VALUES grid stays visually consistent with
 * the rest of the VQE's SQL-aware coloring rather than inventing new colors.
 * `expression` stays neutral (the pre-existing default cell color) since it
 * is the "nothing special" case, not a hue.
 */
export const VALUE_CELL_KIND_TONE_CLASS: Record<ValueCellKind, string> = {
  string: 'text-green-600 dark:text-green-400',
  number: 'text-amber-700 dark:text-amber-400',
  expression: 'text-foreground',
};
